import {
  Component,
  Injector,
  inject,
  input,
  model,
  contentChildren
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { EMPTY, distinctUntilChanged, skip, switchMap } from 'rxjs';

import { IfFeatureService } from '../../../services/if-feature/if-feature.service';
import { MenuService } from '../../../services/menu/menu.service';
import { MobileFlyoutMenuNavComponent } from './mobile-flyout-menu-nav.component';

@Component({
  selector: 'td-wb-mobile-flyout-menu',
  templateUrl: 'mobile-flyout-menu.component.html',
  styleUrls: ['mobile-flyout-menu.component.scss'],
  host: {
    '(click)': 'onClick($event)',
    '[class.td-wb-mobile-flyout-menu--show]': 'opened()',
    '[style.display]': 'opened() ? "block" : "none"',
    '[style.width]': 'opened() ? "100%" : "0"'
  }
})
export class MobileFlyoutMenuComponent {

  readonly opened = model(false);

  readonly menus = contentChildren(MobileFlyoutMenuNavComponent, { descendants: true });

  readonly mainNav = input<MobileFlyoutMenuNavComponent>(undefined);

  private readonly menuService = inject(MenuService);
  private readonly ifFeatureService = inject(IfFeatureService);
  private readonly injector = inject(Injector);
  private readonly chatFeatureEnabled: boolean = this.ifFeatureService.getFeatureFlag('wbrChatEnabled');

  constructor() {
    toObservable(this.mainNav, { injector: this.injector })
      .pipe(
        switchMap((nav: MobileFlyoutMenuNavComponent | undefined) => {
          if (nav === undefined) {
            return EMPTY;
          }

          // Ignore the initial open state on nav replacement to preserve current behavior.
          return toObservable(nav.opened, { injector: this.injector }).pipe(
            distinctUntilChanged(),
            skip(1)
          );
        }),
        takeUntilDestroyed()
      )
      .subscribe((navOpened: boolean): void => {
        if (navOpened) {
          this.open();
        } else {
          this.close();
        }
      });
  }

  onClick(event: MouseEvent): void {
    if (event.target['closest'] !== undefined &&
        event.target['closest']('.td-wb-mobile-flyout-menu--show a[href]') !== null) {
      this.close();
    }
  }

  open(): void {
    this.toggle(true);
    if (this.chatFeatureEnabled) {
      this.menuService.hideFabMenuForMobileMenu();
    }
    this.mainNav().open();
  }

  close(): void {
    this.toggle(false);
    if (this.chatFeatureEnabled) {
      this.menuService.showFabMenuForMobileMenu();
    }
    this.menus().forEach((m: MobileFlyoutMenuNavComponent): void => m.close());
  }

  toggle(isOpen: boolean = !this.opened()): void {
    if (this.opened() !== isOpen) {
      this.opened.set(isOpen);
    }
  }

  onBackdropClicked(): void {
    this.close();
  }
}
